import { instantForWallClock, isValidTimeZone, wallClockIn } from './localTime.js';
/**
 * Promotion dates (Warren, 29 Oct 2026): "Valid until" means the end of that
 * day, 23:59:59 America/Los_Angeles, in Admin and the Api.
 *
 * WHY THIS EXISTS. The Admin coupon form sends a calendar date
 * ("2026-11-13"). The Api stored it with `new Date(value)`, which reads a
 * bare date as midnight UTC: 16:00 on 12 Nov in Los Angeles, or 17:00 the
 * day before in summer time. Every coupon ended 7 to 8 hours before the day
 * it named had even started, and the Admin list (browser zone) and the edit
 * form (the UTC date) showed different days for the same coupon.
 *
 * One zone for every reader: Los Angeles. A driver in Toronto sees the same
 * "Until Nov 13" as an admin in Los Angeles, and the coupon ends at the same
 * instant for both. "Valid from" is the symmetric start of its day.
 *
 * NO FALLBACK. localTime.ts falls back to the server's own clock when a zone
 * is unusable; on a UTC server that would quietly give 23:59:59.999Z. These
 * helpers throw instead, so a runtime without the zone data fails loudly
 * rather than ending a promotion 7 hours early.
 */
export const PROMOTION_TIME_ZONE = 'America/Los_Angeles';
function requireZone() {
    if (!isValidTimeZone(PROMOTION_TIME_ZONE)) {
        throw new Error(`This runtime cannot resolve ${PROMOTION_TIME_ZONE}; promotion dates refuse to guess a zone`);
    }
}
const pad = (n) => String(n).padStart(2, '0');
/** True for a strict YYYY-MM-DD that names a real calendar date. */
export function isCalendarDate(value) {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value))
        return false;
    const [y, m, d] = value.split('-').map(Number);
    const at = new Date(Date.UTC(y, m - 1, d));
    return at.getUTCFullYear() === y && at.getUTCMonth() === m - 1 && at.getUTCDate() === d;
}
function calendarParts(ymd) {
    if (!isCalendarDate(ymd))
        throw new Error(`Not a calendar date (YYYY-MM-DD): ${String(ymd)}`);
    const [year, month, dayOfMonth] = ymd.split('-').map(Number);
    return { year, month, dayOfMonth };
}
/** The Los Angeles calendar date of an instant, as YYYY-MM-DD. What a date
 *  input shows for a stored promotion date. */
export function losAngelesDate(at) {
    requireZone();
    const instant = at instanceof Date ? at : new Date(at);
    if (Number.isNaN(instant.getTime()))
        throw new Error(`Not an instant: ${String(at)}`);
    const c = wallClockIn(instant, PROMOTION_TIME_ZONE);
    return `${c.year}-${pad(c.month)}-${pad(c.dayOfMonth)}`;
}
/** 00:00:00.000 in Los Angeles at the start of `ymd`. Throws on a bad date. */
export function startOfDayLosAngeles(ymd) {
    requireZone();
    const at = instantForWallClock({ ...calendarParts(ymd), hour: 0, minute: 0 }, PROMOTION_TIME_ZONE);
    // The instant reads as `ymd` and the millisecond before it does not.
    if (losAngelesDate(at) !== ymd || losAngelesDate(new Date(at.getTime() - 1)) === ymd) {
        throw new Error(`Could not resolve the start of ${ymd} in ${PROMOTION_TIME_ZONE}`);
    }
    return at;
}
/**
 * 23:59:59.999 in Los Angeles at the end of `ymd`: the next Los Angeles
 * midnight minus 1 ms. Correct across PDT and PST, including the 25-hour
 * fall-back day and the 23-hour spring-forward day: Los Angeles changes its
 * clocks at 02:00, never near 23:59, so 23:59 exists exactly once every day.
 * Throws on a bad date.
 */
export function endOfDayLosAngeles(ymd) {
    requireZone();
    const minute = instantForWallClock({ ...calendarParts(ymd), hour: 23, minute: 59 }, PROMOTION_TIME_ZONE);
    const at = new Date(minute.getTime() + 59_999);
    // The instant reads as `ymd` and the millisecond after it does not.
    if (losAngelesDate(at) !== ymd || losAngelesDate(new Date(at.getTime() + 1)) === ymd) {
        throw new Error(`Could not resolve the end of ${ymd} in ${PROMOTION_TIME_ZONE}`);
    }
    return at;
}
/**
 * The calendar date an Api caller wrote: the leading YYYY-MM-DD of the
 * string, taken as written. '2026-11-13' and '2026-11-13T00:00:00Z' both mean
 * 13 November; the time and zone after the date are ignored, because a
 * promotion runs for whole Los Angeles days. Null when there is no real date.
 */
export function promotionInputDate(input) {
    const m = /^(\d{4}-\d{2}-\d{2})(?:[T ]|$)/.exec(input.trim());
    return m && isCalendarDate(m[1]) ? m[1] : null;
}
function parsePromotionInput(input, toInstant) {
    if (input === undefined)
        return undefined;
    if (input === null || input === '')
        return null;
    if (typeof input !== 'string')
        return 'invalid';
    const ymd = promotionInputDate(input);
    return ymd ? toInstant(ymd) : 'invalid';
}
/** Api input for "Valid until": the end of that Los Angeles day. */
export function parseValidUntilInput(input) {
    return parsePromotionInput(input, endOfDayLosAngeles);
}
/** Api input for "Valid from": the start of that Los Angeles day. */
export function parseValidFromInput(input) {
    return parsePromotionInput(input, startOfDayLosAngeles);
}
/** The Api's refusal for a promotion date it cannot read. */
export const PROMOTION_DATE_MESSAGE = 'Use a date like 2026-11-13. A coupon is valid through 11:59 PM Los Angeles time on its last day.';
/** A stored promotion date shown as its Los Angeles calendar day, in the
 *  reader's language. The driver card and the Admin list use it. */
export function formatLosAngelesDate(at, locale, options = { month: 'short', day: 'numeric', year: 'numeric' }) {
    requireZone();
    return new Date(at).toLocaleDateString(locale, { ...options, timeZone: PROMOTION_TIME_ZONE });
}
/**
 * True once `now` is past a coupon's (or a claim's) end. The end instant
 * itself still counts as valid; an absent end never expires. The claim route
 * and the apply path (session stop, wallet top-up) share this one rule.
 */
export function couponExpired(validUntil, now) {
    if (validUntil === null || validUntil === undefined)
        return false;
    const end = validUntil instanceof Date ? validUntil.getTime() : new Date(validUntil).getTime();
    return end < now.getTime();
}
//# sourceMappingURL=promotionDates.js.map